import { OperationalMode, Environment } from '../contexts/ModelSelectorContext';
import { EnhancedArtifact } from './artifacts';
import { ActionConfig } from './actionConfig';

// Re-export types for use in other files
export type { OperationalMode, Environment } from '../contexts/ModelSelectorContext';

// Card Location Types
export enum CardLocation {
  CONTENT = 'content',    // Thread content section
  CONTEXT = 'context'     // Context panel section
}

// Content Card Types (triggered by chat messages)
export enum ContentCardType {
  ARTIFACT = 'artifact',
  PROCESS = 'process', 
  ARTIFACT_ITEM = 'artifact_item',
  LIST = 'list',
  ARTIFACT_SKILLS = 'artifact_skills',
  PROCESS_SKILLS = 'process_skills',
  PRIME_ARTIFACT = 'prime_artifact'
}

// Context Card Types (triggered by content cards)
export enum ContextCardType {
  DETAIL = 'detail',
  RELATIONSHIPS = 'relationships',
  HISTORY = 'history',
  ANALYTICS = 'analytics',
  PRIME_STATES = 'prime_states',
  PRIME_TRANSITIONS = 'prime_transitions',
  PRIME_PROCESSES = 'prime_processes',
  PRIME_PARAMETERS = 'prime_parameters',
  PRIME_SKILLS = 'prime_skills'
}

// Combined card type
export type CardType = ContentCardType | ContextCardType;

// Card Layout Configuration
export type CardBaseLayout = 'compact' | 'expanded' | 'detailed';

export interface CardStyling {
  backgroundColor: string;
  borderColor: string;
  borderWidth: string;
  borderRadius: string;
  boxShadow: string;
  color: string;
}

export interface CardSection {
  id: string;
  type: 'header' | 'content' | 'actions' | 'metadata' | 'custom';
  roleVisibility: OperationalMode[];  // Which roles see this section
  modelVisibility: Environment[];     // Which models show this section
  component: string;                  // Component name to render
  props?: Record<string, any>;        // Additional props for the component
  order: number;                      // Display order
}

export interface CardLayout {
  baseLayout: CardBaseLayout;
  sections: CardSection[];
  styling: CardStyling;
  maxWidth?: string;
  minHeight?: string;
}

// Card Data Types
export interface ArtifactCardData {
  artifact: EnhancedArtifact;
  selectedArtifactId?: string;
  allArtifacts?: EnhancedArtifact[];
  actionConfig?: ActionConfig; // Optional action configuration for flexible field updates
}

export interface ProcessCardData {
  process: EnhancedArtifact;
  allArtifacts: EnhancedArtifact[];
  executionHistory?: any[];
  performanceMetrics?: any;
}

export interface ArtifactItemCardData {
  artifactItem: any;
  parentArtifact: EnhancedArtifact;
  relationships?: any[];
}

export interface ListCardData {
  items: any[];
  itemType: string;
  filters?: Record<string, any>;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export interface ArtifactSkillsCardData {
  artifactId: string;
  artifactName: string;
  skills?: any[];  // ParameterWithSkill[] from artifactParameter.ts
  canEdit?: boolean;
}

export interface ProcessSkillsCardData {
  processId: string;
  processName: string;
  skills?: any[];  // ProcessSkill[] from process skills
  canEdit?: boolean;
}

export interface PrimeArtifactCardData {
  primeArtifact: any; // PrimeArtifact type
  developmentStatus: string;
  approvalStatus: {
    details: boolean;
    states: boolean;
    transitions: boolean;
    processes: boolean;
  };
  isTemplateReady: boolean;
}

export interface DetailCardData {
  entity: any;
  entityType: string;
  relatedEntities?: any[];
}

export interface RelationshipCardData {
  entity: any;
  relationships: any[];
  relationshipTypes?: string[];
}

export interface HistoryCardData {
  entity: any;
  history: any[];
  historyType: string;
}

export interface AnalyticsCardData {
  entity: any;
  metrics: any;
  chartData?: any[];
  timeRange?: string;
}

// Combined card data type
export type CardData = 
  | ArtifactCardData 
  | ProcessCardData 
  | ArtifactItemCardData 
  | ListCardData 
  | ArtifactSkillsCardData
  | ProcessSkillsCardData
  | PrimeArtifactCardData
  | DetailCardData 
  | RelationshipCardData 
  | HistoryCardData 
  | AnalyticsCardData;

// Card Metadata
export interface CardMetadata {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
  tags?: string[];
  description?: string;
  commandHistory?: {
    command: string;
    args: string[];
    executionTime?: number;
    dataSource?: string;
    success?: boolean;
    error?: string;
  };
}

// Card Context (from thread)
export interface CardContext {
  threadId: string;
  userId: string;
  uiRole: OperationalMode;
  modelType: Environment;
  dataSource: 'sample' | 'live';
}

// Card Triggers
export interface CardTrigger {
  id: string;
  type: 'click' | 'hover' | 'command' | 'auto';
  action: 'show_context' | 'navigate' | 'execute_command' | 'update_card';
  target?: string;
  payload?: any;
}

// Main Composite Card Interface
export interface CompositeCard {
  id: string;
  location: CardLocation;
  type: CardType;
  
  // Context from thread
  context: CardContext;
  
  // Card data
  data: CardData;
  metadata: CardMetadata;
  
  // Layout configuration
  layout: CardLayout;
  
  // Triggers
  triggers: CardTrigger[];
  
  // State
  isCollapsed?: boolean;
  isSelected?: boolean;
  isPinned?: boolean;
}

// Card Component Props
export interface CardComponentProps {
  card: CompositeCard;
  onSelect?: (cardId: string) => void;
  onCollapse?: (cardId: string, collapsed: boolean) => void;
  onPin?: (cardId: string, pinned: boolean) => void;
  onTrigger?: (trigger: CardTrigger) => void;
}

// Card Section Component Props
export interface CardSectionProps {
  section: CardSection;
  card: CompositeCard;
  onAction?: (action: string, payload?: any) => void;
}

// Card Factory Configuration
export interface CardFactoryConfig {
  defaultStyling: Record<CardType, CardStyling>;
  roleSections: Record<OperationalMode, CardSection[]>;
  modelSections: Record<Environment, CardSection[]>;
  componentRegistry: CardComponentRegistry;
}

// Component Registry
export interface CardComponentRegistry {
  baseComponents: {
    [sectionType: string]: React.ComponentType<CardSectionProps>;
  };
  roleComponents: {
    [role in OperationalMode]: {
      [sectionType: string]: React.ComponentType<CardSectionProps>;
    };
  };
  modelComponents: {
    [model in Environment]: {
      [sectionType: string]: React.ComponentType<CardSectionProps>;
    };
  };
  cardTypeComponents: {
    [cardType: string]: {
      [sectionType: string]: React.ComponentType<CardSectionProps>;
    };
  };
}

// Card Agent Interface
export interface ChatCardAgent {
  // Card Management
  createCard(type: CardType, data: CardData, context: CardContext): Promise<CompositeCard>;
  updateCard(cardId: string, updates: Partial<CompositeCard>): Promise<CompositeCard>;
  deleteCard(cardId: string): Promise<void>;
  
  // Card Operations
  getCardsByThread(threadId: string, filters?: CardFilters): Promise<CompositeCard[]>;
  pinCard(cardId: string): Promise<void>;
  unpinCard(cardId: string): Promise<void>;
  
  // Card Registry
  registerCardComponent(type: string, component: React.ComponentType, role?: OperationalMode, model?: Environment): void;
  getCardComponent(type: string, role?: OperationalMode, model?: Environment): React.ComponentType | null;
  
  // Integration
  addCardToMessage(messageId: string, card: CompositeCard): Promise<void>;
  syncWithChatContext(): Promise<void>;
}

// Card Filters
export interface CardFilters {
  location?: CardLocation;
  type?: CardType;
  uiRole?: OperationalMode;
  modelType?: Environment;
  isPinned?: boolean;
  tags?: string[];
}
